import { supabase } from '@/integrations/supabase/client';
import type {
  Invoice,
  InvoiceLineItem,
  InvoicePayment,
  InvoiceTemplate,
  InvoiceTemplateData,
  InvoiceSettings,
  InvoiceFormData,
  InvoiceLineItemFormData,
  InvoicePaymentFormData,
  InvoiceCalculation
} from '@/types/invoice.types';
import type { Database } from '@/integrations/supabase/types';

type ProjectMilestone = Database['public']['Tables']['project_milestones']['Row'];

// Automated billing interfaces
export interface BillingWorkflowConfig {
  auto_generate_invoices: boolean;
  approval_threshold: number;
  payment_terms_days: number;
  late_fee_percentage: number;
  reminder_days_before_due: number[];
  auto_send_reminders: boolean;
}

export interface InvoiceGenerationResult {
  success: boolean;
  invoice_id?: string;
  invoice_number?: string;
  amount?: number;
  error?: string;
}

// Invoice CRUD operations
export async function fetchInvoices(filters?: {
  organization_id?: string;
  project_id?: string;
  status?: string[];
  date_from?: string;
  date_to?: string;
}) {
  let query = supabase
    .from('invoices')
    .select(`
      *,
      organization:organizations(id, name),
      project:projects(id, name),
      line_items:invoice_line_items(count),
      payments:invoice_payments(amount)
    `)
    .order('created_at', { ascending: false });

  if (filters?.organization_id) {
    query = query.eq('organization_id', filters.organization_id);
  }
  if (filters?.project_id) {
    query = query.eq('project_id', filters.project_id);
  }
  if (filters?.status && filters.status.length > 0) {
    query = query.in('status', filters.status);
  }
  if (filters?.date_from) {
    query = query.gte('issue_date', filters.date_from);
  }
  if (filters?.date_to) {
    query = query.lte('issue_date', filters.date_to);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function fetchInvoiceById(id: string) {
  const { data, error } = await supabase
    .from('invoices')
    .select(`
      *,
      organization:organizations(*),
      project:projects(*),
      contract:generated_contracts(*),
      line_items:invoice_line_items(*),
      payments:invoice_payments(*)
    `)
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Invoice;
}

export async function createInvoice(data: InvoiceFormData) {
  // Generate invoice number
  const { data: invoiceNumber, error: invoiceError } = await supabase.rpc('generate_invoice_number', {
    org_id: data.organization_id
  });

  if (invoiceError) throw invoiceError;

  // Create the invoice
  const { data: newInvoice, error: createError } = await supabase
    .from('invoices')
    .insert({
      invoice_number: invoiceNumber,
      organization_id: data.organization_id,
      project_id: data.project_id,
      contract_id: data.contract_id,
      issue_date: data.issue_date,
      due_date: data.due_date,
      tax_rate: data.tax_rate,
      discount_rate: data.discount_rate,
      currency: data.currency,
      notes: data.notes,
      payment_terms: data.payment_terms,
      status: 'draft'
    })
    .select()
    .single();

  if (createError) throw createError;

  // Create line items
  if (data.line_items.length > 0) {
    const lineItems = data.line_items.map((item, index) => ({
      invoice_id: newInvoice.id,
      description: item.description,
      quantity: item.quantity,
      unit_price: item.unit_price,
      amount: item.quantity * item.unit_price,
      tax_rate: item.tax_rate,
      project_milestone_id: item.project_milestone_id,
      project_expense_id: item.project_expense_id,
      sort_order: index
    }));

    const { error: lineItemsError } = await supabase
      .from('invoice_line_items')
      .insert(lineItems);

    if (lineItemsError) throw lineItemsError;
  }

  // Calculate and update totals
  await updateInvoiceTotals(newInvoice.id);

  return fetchInvoiceById(newInvoice.id);
}

export async function updateInvoice(id: string, updates: Partial<Invoice>) {
  const { data, error } = await supabase
    .from('invoices')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteInvoice(id: string) {
  const { error } = await supabase
    .from('invoices')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// Line item operations
export async function addLineItem(invoiceId: string, item: InvoiceLineItemFormData) {
  const { data, error } = await supabase
    .from('invoice_line_items')
    .insert({
      invoice_id: invoiceId,
      description: item.description,
      quantity: item.quantity,
      unit_price: item.unit_price,
      amount: item.quantity * item.unit_price,
      tax_rate: item.tax_rate,
      project_milestone_id: item.project_milestone_id,
      project_expense_id: item.project_expense_id
    })
    .select()
    .single();

  if (error) throw error;

  // Update invoice totals
  await updateInvoiceTotals(invoiceId);

  return data;
}

export async function updateLineItem(id: string, updates: Partial<InvoiceLineItem>) {
  // Recalculate amount if quantity or unit_price changed
  if (updates.quantity !== undefined || updates.unit_price !== undefined) {
    const { data: currentItem } = await supabase
      .from('invoice_line_items')
      .select('quantity, unit_price')
      .eq('id', id)
      .single();

    if (currentItem) {
      const quantity = updates.quantity ?? currentItem.quantity;
      const unitPrice = updates.unit_price ?? currentItem.unit_price;
      updates.amount = quantity * unitPrice;
    }
  }

  const { data, error } = await supabase
    .from('invoice_line_items')
    .update(updates)
    .eq('id', id)
    .select('invoice_id')
    .single();

  if (error) throw error;

  // Update invoice totals
  await updateInvoiceTotals(data.invoice_id);

  return data;
}

export async function deleteLineItem(id: string) {
  // Get invoice ID before deletion
  const { data: lineItem } = await supabase
    .from('invoice_line_items')
    .select('invoice_id')
    .eq('id', id)
    .single();

  const { error } = await supabase
    .from('invoice_line_items')
    .delete()
    .eq('id', id);

  if (error) throw error;

  // Update invoice totals
  if (lineItem) {
    await updateInvoiceTotals(lineItem.invoice_id);
  }
}

// Payment operations
export async function addPayment(invoiceId: string, payment: InvoicePaymentFormData) {
  const { data, error } = await supabase
    .from('invoice_payments')
    .insert({
      invoice_id: invoiceId,
      ...payment
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updatePayment(id: string, updates: Partial<InvoicePayment>) {
  const { data, error } = await supabase
    .from('invoice_payments')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deletePayment(id: string) {
  const { error } = await supabase
    .from('invoice_payments')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// Settings operations
export async function fetchInvoiceSettings(organizationId: string) {
  const { data, error } = await supabase
    .from('invoice_settings')
    .select('*')
    .eq('organization_id', organizationId)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data;
}

export async function updateInvoiceSettings(
  organizationId: string,
  settings: Partial<InvoiceSettings>
) {
  const { data, error } = await supabase
    .from('invoice_settings')
    .upsert({
      organization_id: organizationId,
      ...settings
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Status operations
export async function sendInvoice(id: string) {
  return updateInvoice(id, { status: 'sent' });
}

export async function markInvoiceViewed(id: string) {
  const { data: invoice } = await fetchInvoiceById(id);
  if (invoice?.status === 'sent') {
    return updateInvoice(id, { status: 'viewed' });
  }
}

export async function cancelInvoice(id: string) {
  return updateInvoice(id, { status: 'cancelled' });
}

// Calculation helpers
export async function updateInvoiceTotals(invoiceId: string) {
  const { data, error } = await supabase.rpc('calculate_invoice_totals', {
    invoice_id: invoiceId
  });

  if (error) throw error;

  const totals = data[0];
  
  // Update invoice with calculated totals
  await supabase
    .from('invoices')
    .update({
      subtotal: totals.subtotal,
      tax_amount: totals.tax_amount,
      discount_amount: totals.discount_amount,
      total_amount: totals.total_amount
    })
    .eq('id', invoiceId);
}

export function calculateLineItemAmount(quantity: number, unitPrice: number): number {
  return quantity * unitPrice;
}

export function calculateInvoiceTotals(
  lineItems: InvoiceLineItem[],
  taxRate: number = 0,
  discountRate: number = 0
): InvoiceCalculation {
  const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
  const discountAmount = subtotal * discountRate;
  const taxableAmount = subtotal - discountAmount;
  const taxAmount = taxableAmount * taxRate;
  const totalAmount = taxableAmount + taxAmount;

  return {
    subtotal,
    tax_amount: taxAmount,
    discount_amount: discountAmount,
    total_amount: totalAmount
  };
}

// Template operations
export async function fetchInvoiceTemplates(organizationId?: string) {
  let query = supabase
    .from('invoice_templates')
    .select('*')
    .order('name');

  if (organizationId) {
    query = query.eq('organization_id', organizationId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function createInvoiceTemplate(template: Partial<InvoiceTemplate>) {
  const { data, error } = await supabase
    .from('invoice_templates')
    .insert(template)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateInvoiceTemplate(id: string, updates: Partial<InvoiceTemplate>) {
  const { data, error } = await supabase
    .from('invoice_templates')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteInvoiceTemplate(id: string) {
  const { error } = await supabase
    .from('invoice_templates')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// Generate invoice from template
export async function generateInvoiceFromTemplate(templateId: string) {
  const { data: template, error } = await supabase
    .from('invoice_templates')
    .select('*')
    .eq('id', templateId)
    .single();

  if (error) throw error;

  const templateData = template.template_data as InvoiceTemplateData;
  
  return createInvoice({
    organization_id: template.organization_id,
    project_id: template.project_id,
    contract_id: template.contract_id,
    issue_date: new Date().toISOString().split('T')[0],
    due_date: templateData.payment_terms || '30',
    tax_rate: templateData.tax_rate,
    discount_rate: templateData.discount_rate,
    currency: templateData.currency || 'USD',
    notes: templateData.notes,
    payment_terms: templateData.payment_terms,
    line_items: templateData.line_items || []
  });
}

// Automated Billing Functions

/**
 * Generate invoice from completed milestone
 */
export async function generateInvoiceFromMilestone(milestoneId: string): Promise<InvoiceGenerationResult> {
  try {
    // Check if milestone is eligible for billing
    const { data: milestone, error: milestoneError } = await supabase
      .from('project_milestones')
      .select(`
        *,
        project:projects (
          id,
          name,
          project_code,
          organization_id,
          organization:organizations (
            id,
            name,
            contact_email,
            address,
            city,
            state,
            zip,
            country
          )
        )
      `)
      .eq('id', milestoneId)
      .single();

    if (milestoneError) throw milestoneError;

    if (!milestone) {
      return { success: false, error: 'Milestone not found' };
    }

    if (milestone.status !== 'completed') {
      return { success: false, error: 'Milestone must be completed to generate invoice' };
    }

    if (milestone.invoice_sent_date) {
      return { success: false, error: 'Invoice already generated for this milestone' };
    }

    // Generate invoice using the database function
    const { data: invoiceId, error: generateError } = await supabase
      .rpc('generate_invoice_from_milestone', {
        milestone_id: milestoneId
      });

    if (generateError) throw generateError;

    // Get the generated invoice details
    const { data: invoice, error: invoiceError } = await supabase
      .from('invoices')
      .select('id, invoice_number, total_amount')
      .eq('id', invoiceId)
      .single();

    if (invoiceError) throw invoiceError;

    return {
      success: true,
      invoice_id: invoiceId,
      invoice_number: invoice.invoice_number,
      amount: invoice.total_amount
    };

  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to generate invoice'
    };
  }
}

/**
 * Process all completed milestones for automated billing
 */
export async function processAutomatedBilling(organizationId?: string): Promise<InvoiceGenerationResult[]> {
  try {
    // Get billing config (for now using default)
    const config = await getBillingConfig(organizationId);
    
    if (!config.auto_generate_invoices) {
      return [];
    }

    // Find completed milestones without invoices
    let query = supabase
      .from('project_milestones')
      .select(`
        id,
        amount,
        project:projects!inner (
          organization_id
        )
      `)
      .eq('status', 'completed')
      .is('invoice_sent_date', null);

    if (organizationId) {
      query = query.eq('project.organization_id', organizationId);
    }

    const { data: milestones, error } = await query;
    if (error) throw error;

    // Process each milestone
    const results: InvoiceGenerationResult[] = [];
    for (const milestone of milestones || []) {
      const result = await generateInvoiceFromMilestone(milestone.id);
      results.push(result);
      
      // Small delay to avoid overwhelming the system
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return results;
  } catch (error) {
    return [];
  }
}

/**
 * Update invoice statuses based on payments
 */
export async function updateInvoiceStatuses(): Promise<{ updated: number }> {
  try {
    // Get invoices with payments to check status
    const { data: invoices, error } = await supabase
      .from('invoices')
      .select(`
        id,
        total_amount,
        status,
        due_date,
        invoice_payments (
          amount
        )
      `)
      .in('status', ['sent', 'viewed', 'partially_paid']);

    if (error) throw error;

    let updated = 0;

    for (const invoice of invoices || []) {
      const totalPaid = invoice.invoice_payments.reduce((sum: number, payment: InvoicePayment) => sum + payment.amount, 0);
      let newStatus = invoice.status;

      if (totalPaid >= invoice.total_amount) {
        newStatus = 'paid';
      } else if (totalPaid > 0) {
        newStatus = 'partially_paid';
      } else if (new Date(invoice.due_date) < new Date()) {
        newStatus = 'overdue';
      }

      if (newStatus !== invoice.status) {
        await supabase
          .from('invoices')
          .update({ status: newStatus })
          .eq('id', invoice.id);
        
        updated++;
      }
    }

    return { updated };
  } catch (error) {
    return { updated: 0 };
  }
}

/**
 * Get billing configuration for organization
 */
async function getBillingConfig(organizationId?: string): Promise<BillingWorkflowConfig> {
  // In a real implementation, this would come from organization settings
  // For now, return default configuration
  return {
    auto_generate_invoices: true,
    approval_threshold: 5000,
    payment_terms_days: 30,
    late_fee_percentage: 1.5,
    reminder_days_before_due: [7, 3, 1],
    auto_send_reminders: true
  };
}

/**
 * Run all automated billing processes
 */
export async function runAutomatedBillingCycle(): Promise<{
  milestone_invoices: InvoiceGenerationResult[];
  status_updates: { updated: number };
}> {
  const [milestoneInvoices, statusUpdates] = await Promise.all([
    processAutomatedBilling(),
    updateInvoiceStatuses()
  ]);

  return {
    milestone_invoices: milestoneInvoices,
    status_updates: statusUpdates
  };
}

// Financial calculation methods for testing critical business logic
export const invoiceService = {
  /**
   * Calculate subtotal from line items
   */
  calculateSubtotal(lineItems: Array<{ quantity: number | null; unit_price: number | undefined; description: string }>): number {
    if (!lineItems || lineItems.length === 0) return 0.00;
    
    return lineItems.reduce((subtotal, item) => {
      const quantity = item.quantity ?? 0;
      const unitPrice = item.unit_price ?? 0;
      return subtotal + (quantity * unitPrice);
    }, 0);
  },

  /**
   * Calculate tax amount
   */
  calculateTax(subtotal: number, taxRate: number): number {
    return Number((subtotal * taxRate).toFixed(2));
  },

  /**
   * Calculate total amount including tax
   */
  calculateTotal(subtotal: number, tax: number): number {
    return Number((subtotal + tax).toFixed(2));
  },

  /**
   * Calculate remaining balance after payments
   */
  calculateRemainingBalance(invoiceTotal: number, payments: Array<{ amount: number }>): number {
    const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
    return Number((invoiceTotal - totalPaid).toFixed(2));
  },

  /**
   * Calculate payment percentage
   */
  calculatePaymentPercentage(invoiceTotal: number, payments: Array<{ amount: number }>): number {
    if (invoiceTotal === 0) return 0;
    const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
    return Number(((totalPaid / invoiceTotal) * 100).toFixed(1));
  },

  /**
   * Format currency amount
   */
  formatCurrency(amount: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  },

  /**
   * Calculate milestone amount based on percentage
   */
  calculateMilestoneAmount(projectValue: number, percentage: number): number {
    return Number((projectValue * (percentage / 100)).toFixed(2));
  },

  /**
   * Calculate percentage discount
   */
  calculatePercentageDiscount(amount: number, discountPercentage: number): number {
    return Number((amount * (discountPercentage / 100)).toFixed(2));
  },

  /**
   * Apply flat discount
   */
  applyFlatDiscount(amount: number, flatDiscount: number): number {
    const result = amount - flatDiscount;
    return Math.max(0, Number(result.toFixed(2))); // Don't allow negative
  },

  /**
   * Calculate monthly recurring amount from annual
   */
  calculateMonthlyRecurring(annualAmount: number): number {
    return Number((annualAmount / 12).toFixed(2));
  },

  /**
   * Calculate prorated amount for partial period
   */
  calculateProratedAmount(periodAmount: number, daysUsed: number, totalDays: number): number {
    return Number((periodAmount * (daysUsed / totalDays)).toFixed(2));
  },

  /**
   * Validate invoice data before calculations
   */
  validateInvoiceData(invoiceData: InvoiceFormData | Invoice): void {
    if (!invoiceData) {
      throw new Error('Invoice data is required');
    }
    
    if (invoiceData.line_items === null || invoiceData.line_items === undefined) {
      throw new Error('Line items are required');
    }
    
    if (typeof invoiceData.tax_rate === 'string' && isNaN(Number(invoiceData.tax_rate))) {
      throw new Error('Tax rate must be a valid number');
    }
  }
};